/* storybook-check-ignore */
import { useMemo } from 'react';

import {
  Box,
  Lightbox as BricksNextLightbox,
  Button,
  FavoriteButton,
  Text,
} from '@opendoor/bricks-next';
import { formatCurrency } from '@opendoor/bricks/helpers/currencyHelpers';

import { isBuyDirectEligible } from 'components/marketplace/helpers';
import { TOUR_ACTION_TEXT } from 'components/marketplace/helpers/propertyActionability';
import { PropertyDetailsType } from 'components/marketplace/property';
import { useTouring } from 'components/self-tour/hooks/useTouring';

import { TourWithAgentModal } from './PropertySection/TourWithAgentModal';

interface LightboxProps {
  handleClose: () => void;
  isOpen: boolean;
  property: PropertyDetailsType;
  selectedPhotoIndex: number;
}

export const Lightbox = ({ handleClose, isOpen, property, selectedPhotoIndex }: LightboxProps) => {
  let displayPrice = 'Price not available';
  if (
    isBuyDirectEligible(property) &&
    property.displayBadge === 'ON_THE_MARKET' &&
    property.buyDirectPrice
  ) {
    displayPrice = formatCurrency(property.buyDirectPrice);
  } else if (property?.listPrice) {
    displayPrice = formatCurrency(property.listPrice);
  }

  const LightboxHeader = useMemo(() => {
    return (
      <Box flexDirection="column" alignItems="center" flex={1}>
        <Text tag="span" typography="$labelMedium" color="$contentInversePrimary">
          {displayPrice}
        </Text>
        <Text tag="span" typography="$bodyXsmall" color="$contentInversePrimary" ml="$2x">
          {property.street}
        </Text>
      </Box>
    );
  }, [property]);

  const LightboxFooter = useMemo(() => {
    return (
      <Box
        flexDirection="row"
        justifyContent="space-between"
        pt="$4x"
        px="$12x"
        $smallerThanMD={{ display: 'none' }}
        width="100%"
      >
        <Box flexDirection="row" alignItems="center" flex={1}>
          <Text tag="span" typography="$labelMedium" color="$contentInversePrimary">
            {displayPrice}
          </Text>
          <Text tag="span" typography="$bodyMedium" color="$contentInversePrimary" ml="$2x">
            — {property.street}, {property.city}, {property.state} {property.zip}
          </Text>
        </Box>
        <Box alignSelf="flex-end" alignItems="center" justifyContent="flex-end" flexDirection="row">
          <FavoriteButton
            analyticsName="cosmos-marketplace-pdp-lightbox-favorite-property-button"
            onPress={property?.onClickReaction}
            size="medium"
            variant="inverse"
            mx="$12x"
            isFavorited={property?.isLiked ?? false}
          />
          <TourButton property={property} />
        </Box>
      </Box>
    );
  }, [property]);

  return (
    <BricksNextLightbox
      analyticsName="cosmos-marketplace-property-photo-lightbox"
      footer={LightboxFooter}
      handleClose={handleClose}
      isOpen={isOpen}
      mobileTitle={LightboxHeader}
      photoUrls={property?.photos ?? []}
      selectedPhotoIndex={selectedPhotoIndex}
    />
  );
};

export default Lightbox;

interface TourButtonProps {
  property: PropertyDetailsType;
}
const TourButton: React.FC<TourButtonProps> = ({ property }) => {
  const touringInfo = useTouring(property);
  if (!touringInfo) return null;

  const { tourKind, tourUrl, handleSelfTour, closeDialog, isDialogOpen } = touringInfo;

  return (
    <>
      <Button
        analyticsName="cosmos-marketplace-pdp-lightbox-self-tour-button"
        label={TOUR_ACTION_TEXT[tourKind]}
        onPress={handleSelfTour}
        href={tourKind === 'selfTour' ? tourUrl : undefined}
        target={tourKind === 'selfTour' ? '_blank' : undefined}
        size="small"
        variant="secondaryInverse"
      />
      {tourKind === 'tourWithAgent' && (
        <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={closeDialog} />
      )}
    </>
  );
};
